import { useCallback, useEffect, useMemo, useRef, useState, ReactNode } from "react"
import { Native } from "../App/Native"
import { GetApp } from "../App"
import { Client } from "../App/Client"
import { ShipContext, ShipContextProps } from "../App/Context"
import { localStorageId } from "../App/LocalStorage"
import { Session } from "../App/Session"
import { ShipSelectViewData } from "./ShipSelect/Context"
import { invalidateCompanionConnect } from "./ShipSelect/Connect"
import { Router, RouterRouteParams, useRouter } from "../App/Routes"
import { useOverlay } from "../App/useOverlay"
import { subscriptionRendersInApp } from "../App/IAP"
import { AppData } from "../App/AppData"
import { accountSessionIdentity } from "../Data/AccountSession"
import { useSubscription, useSocket } from "../App/useSubscription"
import { Block } from "../Components/Block"
import { Loading } from "../Components/Loading"
import { IShip, IShipUser, IShipUserLevel } from "../Types"
import { Subscription } from "../Types/Generated/LogbookApi/Subscriptions"
import { AppDisconnectedView } from "./App/DisconnectedView"
import { AppRoutes, AppViewRoutes } from "./App/Router"
import { AppViewData, AppViewState, AppViewContext } from "./App/Context"
import { AppViewProps } from "./App/Params"
import { ChangeLogView } from "./ChangeLogView"
import { AppHeaderView } from "./AppHeaderView"
import { ShipView } from "./ShipView"
import { AccountView } from "./AccountView"
import { AccountSettingsView } from "./AccountSettingsView"
import { AppOverlayContent, AppOverlayContentOptions, AppOverlayContentView } from "./AppOverlayContentView"
import { SessionExpiredViewData } from "./App/SessionExpiredView"
import { ShipEditView } from "./ShipEditView"
import { ShipImportView } from "./ShipImportView"
import { UserSwitchViewData } from "./Ship/UserSwitchView"
import { logger, NATIVE } from "../App/Logger"
import { apiErrorReason } from "../App/ApiError"
import { useSelectedShip, withSelectedShipData } from "./App/SelectedShip"
import { useAllShips, withAllShipsData } from "./App/AllShips"

export { AppRoutes, AppViewRoutes } from "./App/Router"
export { AppViewData, AppViewContext, GetAppView } from "./App/Context"
export type { AppViewState } from "./App/Context"

function AppViewUI({ route }: AppViewProps) {
  const app = GetApp()!;
  const [connected, setConnected] = useState<boolean>(app.socket && !app.socket.disconnected);
  const selectedShip = useSelectedShip();
  const { ship, shipUser, shipUsers, reset: resetSelectedShip } = selectedShip;
  const { ships, membership, ownShipId, load: loadShips } = useAllShips();
  const [overlay, overlayOptions, setOverlay, resetOverlay] = useOverlay<AppOverlayContent, AppOverlayContentOptions>(AppOverlayContent.none);
  // Closing a ship makes a full cloud round trip (see `ShipApi.swift`'s close
  // branch), so the confirm dialog needs to say so and must not be re-submittable
  // meanwhile -- and a failure has to be visible rather than swallowed (#1279).
  const [shipClosing, setShipClosing] = useState(false);
  const [shipCloseFailed, setShipCloseFailed] = useState(false);
  // The HTTP status of a failed close, so the dialog can name the cause instead of
  // blaming the internet whatever happened (#1453).
  const [shipCloseFailedStatus, setShipCloseFailedStatus] = useState<number | undefined>(undefined);
  const [companionReturnRequested] = useState(() => !!new URLSearchParams(window.location.search).get("companion_disconnected"));
  const [companionDisconnectState, setCompanionDisconnectState] = useState<"pending" | "failed" | undefined>(() => companionReturnRequested ? "pending" : undefined);
  // The ShipUser new attributed entries are written as, when it differs from the real
  // `shipUser` above (#197/#1026 (tracker #827) user switch). See AppViewState.actingShipUser for why
  // this lives here rather than ShipViewContext.
  const [actingShipUserId, setActingShipUserId] = useState<number | undefined>(undefined);
  const actingShipUser = useMemo(
    () => shipUsers?.find((user) => user.id == actingShipUserId),
    [actingShipUserId, shipUsers],
  );

  const platform = useMemo(() => {
    return AppViewData.platform(app.cloudSession(), app.appStyles);
  }, [app]);

  const navigateApp = useMemo(() => {
    return Router.navigate(AppViewRoutes, app.updateRoute);
  }, []);

  const resetState = useCallback(() => {
    resetSelectedShip();
    // A previous ship's acting ShipUser id is meaningless once a different ship (or none)
    // is open -- clear it along with the rest of the per-ship state (#197/#1026).
    setActingShipUser(undefined);
  }, [resetSelectedShip]);

  // Sets which ShipUser new attributed entries (log/note) are written as. Only ever
  // changes attribution -- Client.setActingShipUserId attaches the override as a header
  // on every subsequent API call, validated + re-derived server-side per request
  // (requireActingShipUserId); `apiUser`/`requireUserLevel` below always keep resolving
  // from the real `shipUser`, so the permission ceiling can never be exceeded by picking
  // another ShipUser here (#197/#1026 user switch).
  const setActingShipUser = useCallback((user?: IShipUser) => {
    setActingShipUserId(user?.id);
    Client.setActingShipUserId(user?.id);
  }, []);

  // Auto-clear: once the current day changes, re-check whether the acting ShipUser is
  // still on that day's crew/skipper roster (the same eligibility rule the picker itself
  // uses) and drop the override if not -- e.g. a new day starts without them aboard.
  useEffect(() => {
    if (actingShipUserId == undefined) {
      return;
    }
    let cancelled = false;
    UserSwitchViewData.loadEligibleShipUserIds(app.apiClient).then((ids) => {
      if (!cancelled && !ids.includes(actingShipUserId)) {
        setActingShipUser(undefined);
      }
    }).catch((error) => logger.error("AppView acting ship user auto-clear", error));
    return () => { cancelled = true; };
  }, [app.current?.dayId]);

  useEffect(() => {
    if (actingShipUserId != undefined && selectedShip.bundleLoaded && !actingShipUser) {
      setActingShipUser(undefined);
    }
  }, [actingShipUser, actingShipUserId, selectedShip.bundleLoaded, setActingShipUser]);

  useEffect(() => {
    setActingShipUser(undefined);
  }, [selectedShip.shipId, setActingShipUser]);

  const evictingShipId = useRef<number | undefined>(undefined);

  const selectShip = useCallback(async (shipId: number) => {
    if (!app.cloudSession()) {
      state.setAppOverlay(AppOverlayContent.selectShip);
      try {
        await app.setShipId(shipId);
        resetState();
      } catch (e) {
        logger.debug("Failed to select ship " + shipId);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
        const error = e as any;
        if (SessionExpiredViewData.isSessionExpired(error)) {
          // The cloud refused the app's session, verified by the app against
          // `GET /api/session` before it names it (#1449). A re-login prompt is the only
          // thing that helps, and the user stays put -- no redirect to account settings,
          // which is where this used to dump them with an unreadable 500.
          state.setAppOverlay(AppOverlayContent.sessionExpired);
          return;
        }
        // The app serialises `LogbookApiError` as `{error: "<case name>", status}` (see
        // `LogbookApi/Api.swift`); `apiErrorReason` is the single reader of that shape
        // (#1449, #1820). This used to test `error.error == true && error.reason == ...`
        // -- a shape that never occurs -- so the specific "ship limit reached" message
        // could not be shown, and every failure fell into the generic arm below.
        const reason = apiErrorReason(error);
        if (reason === "limitActiveShipsReached") {
          state.setAppOverlay(AppOverlayContent.selectShipFailed, { selectShipError: reason });
        } else {
          state.setAppOverlay(AppOverlayContent.selectShipFailed);
        }
        navigateApp(AppRoutes.accountSettings);
        return;
      }
      resetOverlay();
    }
    if (evictingShipId.current == shipId) {
      return;
    }
    logger.debug("redirect to sync");
    if (app.cloudSession()) {
      navigateApp(AppRoutes.ship, { shipId: shipId });
    } else {
      navigateApp(AppRoutes.ship, { shipId: shipId, shipRoute: "sync" });
    }
  }, []);

  const resetShip = useCallback(async () => {
    if (app.shipId && !app.cloudSession()) {
      setShipCloseFailed(false);
      setShipCloseFailedStatus(undefined);
      setShipClosing(true);
      try {
        await app.setShipId(app.shipId, true);
      } catch (error) {
        // Surface the failure instead of returning silently: the overlay used to
        // stay open with no message at all, which is indistinguishable from a
        // button that does nothing (#1279). The dialog stays open so the user can
        // retry rather than being dropped somewhere with the ship still claimed.
        logger.debug("Failed to reset ship", app.shipId, error);
        setShipCloseFailedStatus((error as { httpStatus?: number } | undefined)?.httpStatus);
        setShipCloseFailed(true);
        return;
      } finally {
        setShipClosing(false);
      }
      resetState();
      resetOverlay();
      await loadShips(true);
      navigateApp(AppRoutes.accountSettings);
    }
    if (app.cloudSession()) {
      resetState();
      resetOverlay();
      await loadShips(true);
      navigateApp(AppRoutes.accountSettings);
    }
  }, [app.shipId, app.cloudSession, app.setShipId, loadShips, navigateApp, resetOverlay, resetState]);

  const forceCloseShip = useCallback(async () => {
    if (!app.shipId || app.cloudSession()) {
      return;
    }
    setShipCloseFailed(false);
    setShipCloseFailedStatus(undefined);
    setShipClosing(true);
    try {
      await app.forceCloseShip(app.shipId);
    } catch (error) {
      logger.debug("Failed to force close ship", app.shipId, error);
      setShipCloseFailedStatus((error as { httpStatus?: number } | undefined)?.httpStatus);
      setShipCloseFailed(true);
      return;
    } finally {
      setShipClosing(false);
    }
    resetState();
    resetOverlay();
    await loadShips(true);
    navigateApp(AppRoutes.accountSettings);
  }, [app.shipId, app.cloudSession, app.forceCloseShip, loadShips, navigateApp, resetOverlay, resetState]);

  const deselectShip = useCallback(async (shipId: number) => {
    if (app.cloudSession()) {
      await AppViewData.closeShip(app.apiClient, shipId);
      await loadShips(true);
      state.resetAppOverlay();
    }
  }, []);

  const socketSubscription = useCallback(async (status: boolean) => {
    setConnected(status);
    if (connected && status == false) {
      await Native.restartNative();
    }
  }, [connected]);
  useSocket(socketSubscription);

  // Shared eviction path for "I have no more effective access to this ship" --
  // used when a ship is explicitly closed (`Subscription.shipClose` below),
  // when a `Subscription.ShipUser` broadcast reveals my own
  // removal/deactivation/downgrade to `none` while paired, and when a
  // `Subscription.Ship` broadcast reveals the ship itself was deleted (#1102).
  // Resets local ship state, then either logs out (local/companion session --
  // there is no "just go back to ship select" for a device-bound session) or
  // navigates to `accountSettings` (cloud session).
  const evictFromShip = async (evictedShipId: number) => {
    try {
      await app.setShipId(evictedShipId, true, true);
    } catch (error) {
      logger.error("Failed to clear selected ship during eviction", error);
    }
    resetState();
    if (app.localSession()) {
      await app.logout();
    } else {
      navigateApp(AppRoutes.accountSettings);
      try {
        await loadShips(true);
      } catch (error) {
        logger.error("Failed to refresh ships after eviction", error);
      }
    }
  };

  useEffect(() => {
    if (!selectedShip.bundleLoaded || selectedShip.shipId == undefined) {
      return;
    }
    const noAccess = selectedShip.activeShipUserId != undefined
      && (!shipUser
        || shipUser.deletedAt != undefined
        || shipUser.active < 1
        || shipUser.shipUserLevel == IShipUserLevel.none);
    if (ship && !noAccess) {
      evictingShipId.current = undefined;
      return;
    }
    if (evictingShipId.current == selectedShip.shipId) {
      return;
    }
    logger.debug(
      "Evicting selected ship after access state changed",
      selectedShip.shipId,
      selectedShip.activeShipUserId,
      ship?.id,
      shipUser?.id,
      shipUser?.active,
      shipUser?.shipUserLevel,
    );
    evictingShipId.current = selectedShip.shipId;
    void evictFromShip(selectedShip.shipId)
      .catch((error) => logger.error("Failed to evict from selected ship", error));
  }, [
    selectedShip.activeShipUserId,
    selectedShip.bundleLoaded,
    selectedShip.shipId,
    ship,
    shipUser,
  ]);

  useSubscription(async (closedShip: IShip, _type: string) => {
    if (closedShip.id == shipId || closedShip.id == app.shipId) {
      await evictFromShip(closedShip.id);
    }
  }, Subscription.shipClose);

  const [content, routeName, routeParams] = useRouter<ReactNode>(route, AppViewRoutes, (router) => {
    router.on(AppRoutes.launchShip, (_params) => {
      return (
        <Block>
          <Loading page />
        </Block>
      );
    });
    router.on(AppRoutes.importShip, (_params) => {
      return <ShipImportView cancelEdit={() => navigateApp(AppRoutes.accountSettings)} />;
    });
    router.on(AppRoutes.newShip, (_params) => {
      return <ShipEditView cancelEdit={() => navigateApp(AppRoutes.accountSettings)} key={"new"} />;
    });
    router.on(AppRoutes.ship, (params) => {
      params.requireInt("shipId");
      params.requireRoute("shipRoute");
      return <></>;
    });
    router.on(AppRoutes.accountSubscriptionComplete, (params) => {
      const sessionId = params.requireString("sessionId");
      if (app.account) {
        return <AccountView account={app.account} backAction={() => navigateApp(AppRoutes.accountSettings)} checkoutSessionId={sessionId} />;
      }
      // The account is loaded asynchronously (loadSession); show a loading page until
      // it arrives instead of throwing, so navigating here before the session settles
      // (e.g. right after app launch) doesn't crash the whole app.
      return <Loading page />;
    });
    router.on(AppRoutes.accountSubscription, (_params) => {
      if (!subscriptionRendersInApp(app)) {
        navigateApp(AppRoutes.accountSettings);
        return <></>;
      }
      if (app.account) {
        return <AccountView account={app.account} backAction={() => navigateApp(AppRoutes.accountSettings)} />;
      }
      // The account is loaded asynchronously (loadSession); show a loading page until
      // it arrives instead of throwing, so navigating here before the session settles
      // (e.g. right after app launch) doesn't crash the whole app.
      return <Loading page />;
    });
    router.on(AppRoutes.accountSettings, (_params) => {
      return <AccountSettingsView cancelEdit={() => { if (!app.cloudSession() && app.shipId) { navigateApp(AppRoutes.ship, { shipId: app.shipId }); } }} ships={ships} companionDisconnectState={companionDisconnectState} />;
    });
    router.on(AppRoutes.signup, (_params) => {
      return <Loading page />;
    });
    router.on(AppRoutes.launch, (_params) => {
      return <Loading page />;
    });
    router.on(AppRoutes.index, (_params) => {
      return <Loading page />;
    });
    router.on(AppRoutes.unknown, (_params) => {
      return <Loading page />;
    });
  });

  const shipId = useMemo(() => {
    if (routeName == AppRoutes.ship) {
      return new RouterRouteParams(routeParams).requireInt("shipId");
    }
    return undefined;
  }, [routeName, routeParams]);
  const renderedContent = routeName == AppRoutes.ship && shipId != undefined
    ? selectedShip.bundleLoaded && ship?.id == shipId
      ? (
          <ShipView
            key={shipId}
            route={{ route: new RouterRouteParams(routeParams).requireRoute("shipRoute") }}
            shipId={shipId}
            users={shipUsers as IShipUser[] | undefined}
            actingShipUser={actingShipUser}
            setActingShipUser={setActingShipUser}
          />
        )
      : <Loading page />
    : content;

  const localAccountSession = !app.cloudSession() && !app.localSession();
  useEffect(() => {
    if (!companionReturnRequested || !localAccountSession || !app.session?.authorized || !app.session.id) {
      return;
    }
    let disposed = false;
    const companionShipId = localStorageId("logbook-companion");
    const finishReturn = () => {
      const params = new URLSearchParams(window.location.search);
      params.delete("companion_disconnected");
      const query = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
      setCompanionDisconnectState(undefined);
      if (routeName !== AppRoutes.accountSettings) {
        navigateApp(AppRoutes.accountSettings);
      }
    };
    if (companionShipId == undefined) {
      finishReturn();
      return;
    }
    if (app.session.shipId === companionShipId) {
      localStorage.removeItem("logbook-companion");
      finishReturn();
      return;
    }
    invalidateCompanionConnect(companionShipId);
    void (async () => {
      try {
        await ShipSelectViewData.cancelLocal(app.apiClient, companionShipId);
        if (disposed) {
          return;
        }
        if (app.shipId === companionShipId) {
          await app.setShipId(companionShipId, true, true);
        }
        if (disposed) {
          return;
        }
        Session.clearCompanion(companionShipId);
        finishReturn();
      } catch (error) {
        logger.error("Failed to disconnect returned companion pairing", error);
        if (!disposed) {
          setCompanionDisconnectState("failed");
          if (routeName !== AppRoutes.accountSettings) {
            navigateApp(AppRoutes.accountSettings);
          }
        }
      }
    })();
    return () => { disposed = true; };
  }, [companionReturnRequested, localAccountSession, app.session?.authorized, app.session?.id]);

  useEffect(() => {
    // The authenticated return effect owns cleanup and routing for this journey.
    if (companionReturnRequested) {
      return undefined;
    }
    const companionShipId = localStorageId("logbook-companion");
    const staleCompanionSelection = !app.cloudSession() && !app.localSession()
      && companionShipId != undefined && app.shipId === companionShipId
      && app.session?.shipId !== companionShipId;
    if (staleCompanionSelection) {
      void Promise.resolve(app.setShipId(companionShipId, true, true)).catch((error) => logger.error("Failed to clear incomplete companion selection", error));
    }
    if (routeName == AppRoutes.index || routeName == AppRoutes.signup) {
      if (!app.cloudSession() && app.shipId && !staleCompanionSelection) {
        navigateApp(AppRoutes.ship, { shipId: app.shipId });
      } else {
        navigateApp(AppRoutes.accountSettings);
      }
    } else if (!app.cloudSession() && routeName == AppRoutes.launch) {
      const companionShipId = localStorageId("logbook-companion");
      logger.debug("check companion session", companionShipId);
      if (companionShipId) {
        let disposed = false;
        const launchPath = window.location.pathname;
        const isCurrent = () => !disposed && window.location.pathname === launchPath
          && localStorageId("logbook-companion") === companionShipId;
        void ShipSelectViewData.prepareHandoff(app.apiClient, companionShipId).then(({ url }) => {
          if (!url) {
            throw new Error("Companion preparation returned no destination");
          }
          if (isCurrent()) {
            window.location.assign(url);
          }
        }).catch((error) => {
          logger.error("Failed to prepare saved companion connection", error);
          if (isCurrent()) {
            window.location.assign("/app/account?companion_error=unreachable");
          }
        });
        return () => { disposed = true; };
      } else if (app.shipId) {
        navigateApp(AppRoutes.ship, { shipId: app.shipId });
      } else {
        navigateApp(AppRoutes.accountSettings);
      }
    } else if (routeName == AppRoutes.unknown) {
      navigateApp(AppRoutes.accountSettings);
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (routeName == AppRoutes.launchShip) {
      logger.debug("Launching ship", routeParams, NATIVE);
      const shipId = new RouterRouteParams(routeParams).requireInt("shipId");
      if (!shipId) {
        navigateApp(AppRoutes.accountSettings);
        return;
      }
      if (ships == undefined) {
        loadShips();
      } else if (shipId == app.shipId) {
        navigateApp(AppRoutes.ship, { shipId: shipId, shipRoute: "sync" });
      } else if (app.shipId == undefined) {
        selectShip(shipId);
      } else {
        navigateApp(AppRoutes.accountSettings);
      }
    }
  }, [ships]);

  useEffect(() => {
    if (app.cloudSession() && routeName != AppRoutes.ship && ship) {
      resetState();
    }
  }, [ship, routeName]);

  // The account is loaded once at launch by loadSession; in the native app it comes from
  // the on-device DB and can still be unsynced then, leaving `app.account` undefined. The
  // subscription route is account-gated, so landing there without an account would hang on
  // Loading until a manual reload. Re-fetch the session once when that happens (authorized,
  // non-cloud session) so the view self-heals.
  useEffect(() => {
    const needsAccount = routeName == AppRoutes.accountSubscription || routeName == AppRoutes.accountSubscriptionComplete;
    if (!needsAccount || app.account || app.cloudSession() || !app.session || app.session.authorized != true) {
      return;
    }
    let cancelled = false;
    const requestIdentity = accountSessionIdentity(app.session, app.sessionType, app.apiClient.target);
    AppData.loadSession(app.apiClient).then((data) => {
      if (!cancelled && data) {
        app.reconcileSession(data, requestIdentity);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [routeName, app.account, app.session, app.sessionType, app.apiClient]);

  const requireUserLevel = useCallback((level: string) => {
    return AppViewData.requireUserLevel(level, shipUser);
  }, [shipUser]);

  const apiUser = useMemo(() => AppViewData.apiUser({
    accountId: app.account?.id,
    accountUserLevel: app.accountUserLevel,
    shipUser: shipUser,
    membership: membership,
  }), [app.account, app.accountUserLevel, shipUser, membership]);

  const context: ShipContextProps = {
    ship: ship,
    shipUser: shipUser,
    requireUserLevel: requireUserLevel,
    apiUser: apiUser,
  };

  const state: AppViewState = {
    connected: connected,
    loadShips: loadShips,
    navigateApp: navigateApp,
    route: route,
    routeName: routeName,
    ships: ships,
    ownShipId: ownShipId,
    membership: membership,
    subscriptionExpired: AppViewData.subscriptionExpired(app.account),
    deselectShip: deselectShip,
    selectShip: selectShip,
    resetShip: resetShip,
    forceCloseShip: forceCloseShip,
    shipClosing: shipClosing,
    shipCloseFailed: shipCloseFailed,
    shipCloseFailedStatus: shipCloseFailedStatus,
    appOverlay: overlay,
    appOverlayOptions: overlayOptions || {},
    setAppOverlay: setOverlay,
    resetAppOverlay: resetOverlay,
    actingShipUser: actingShipUser,
    setActingShipUser: setActingShipUser,
  };

  return (
    <AppViewContext.Provider value={state}>
      <div className="AppView ">
        <AppDisconnectedView connected={connected} />
        <ChangeLogView platform={platform} showExternal={false} />
        <AppOverlayContentView />
        <ShipContext.Provider value={context}>
          <AppHeaderView ship={ship} />
          {renderedContent}
        </ShipContext.Provider>
      </div>
    </AppViewContext.Provider>
  );
}

export const AppView = withSelectedShipData(withAllShipsData(AppViewUI));
